import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { BaseViewStore } from 'core/stores';
import { DashboardService } from 'common/data';
import { isSeller, isSmartshipWarehouseAdministrator, isSuperAdminOrganization, isWarehouse } from 'core/utils';

const WAREHOUSE_COLORS = [
	'#6b7a8d',
	'#e85d75',
	'#4a90d9',
	'#f5a623',
	'#50c7b0',
	'#9b59b6',
	'#e05e5e',
	'#3ec97f',
	'#e8a838',
	'#5b8ef0',
];

const STATUS_KEYS = ['entered', 'assigned', 'picking', 'completed', 'overall'];
const WORKING_STATUSES = ['entered', 'assigned', 'picking'];

const AUTO_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

const toDate = val => (val ? String(val).slice(0, 10) : null);
const todayIso = () => new Date().toISOString().slice(0, 10);

function applyStatusFilter(o, activeStatus) {
	if (activeStatus === 'overall') return true;
	if (activeStatus === 'completed') return !WORKING_STATUSES.includes(o.status);
	return o.status === activeStatus;
}

// shipmentType: 'all' | 'ltl' | 'parcel'. `isLtl` mirrors the BE's FulfillmentOrder.IsLtlShipment().
function applyShipmentType(rows, shipmentType) {
	if (shipmentType === 'ltl') return rows.filter(r => r.isLtl);
	if (shipmentType === 'parcel') return rows.filter(r => !r.isLtl);
	return rows;
}

function applyMetricDate(orders, activeMetric, selectedDate) {
	const today = todayIso();
	return orders
		.filter(o => activeMetric !== 'onHold' || o.isOnHold)
		.filter(o => activeMetric !== 'late' || (o.shipByDate && toDate(o.shipByDate) < today))
		.filter(o => activeMetric !== 'prime' || o.isPrime)
		.filter(o => !selectedDate || toDate(o.shipByDate) === selectedDate);
}

class DashboardViewStore extends BaseViewStore {
	@observable allOrders = [];
	@observable warehouseColorMap = {};
	@observable selectedWarehouse = 'all';
	@observable selectedSeller = 'all';
	@observable selectedShipmentType = 'all';
	@observable activeStatus = 'overall';
	@observable activeMetric = 'all';
	@observable selectedDate = null;
	@observable cbmBreakdown = [];
	@observable completedBreakdown = [];
	@observable completedLoading = true;
	@observable error = null;

	@observable gridViewOpen = false;
	@observable gridOrders = [];
	@observable gridLoading = false;
	@observable gridError = null;

	completedBreakdownReactionDisposer = null;
	dashboardService = null;
	refreshTimerId = null;

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.dashboardService = new DashboardService(this.rootStore.app.baasic.apiClient);
		this.initialize();
		this.startAutoRefresh();
	}

	@computed
	get showWarehouses() {
		return (
			isSuperAdminOrganization(this.rootStore) ||
			isSmartshipWarehouseAdministrator(this.rootStore) ||
			isSeller(this.rootStore)
		);
	}

	@computed
	get showSellers() {
		return (
			isSuperAdminOrganization(this.rootStore) ||
			isSmartshipWarehouseAdministrator(this.rootStore) ||
			isWarehouse(this.rootStore)
		);
	}

	@computed
	get statusKeys() {
		return STATUS_KEYS;
	}

	@action.bound
	async initialize() {
		this.suspend();
		try {
			await Promise.all([this.loadOrdersSummary(), this.loadCbmBreakdown(), this.loadCompletedBreakdown()]);
		} catch (e) {
			runInAction(() => {
				this.error = 'Failed to load dashboard data';
			});
		} finally {
			this.resume();
		}

		// Completed breakdown re-fetches on metric/date changes only.
		// Warehouse/seller filtering happens client-side against the in-memory breakdown.
		this.completedBreakdownReactionDisposer = reaction(
			() => [this.activeMetric, this.selectedDate],
			() => this.loadCompletedBreakdown(),
			{ fireImmediately: false }
		);
	}

	@action.bound
	async loadOrdersSummary() {
		try {
			const res = await this.dashboardService.getOrdersSummary();
			const orders = res.data || [];
			runInAction(() => {
				this.error = null;
				this.allOrders = orders;
				const map = {};
				[...new Map(orders.filter(o => o.warehouseId).map(o => [o.warehouseId, o.warehouseName])).entries()]
					.sort(([, a], [, b]) => a.localeCompare(b))
					.forEach(([id], i) => {
						map[id] = WAREHOUSE_COLORS[(i + 1) % WAREHOUSE_COLORS.length];
					});
				this.warehouseColorMap = map;
			});
		} catch (e) {
			runInAction(() => {
				this.error = 'Failed to load dashboard data';
			});
		}
	}

	@action.bound
	async loadCbmBreakdown() {
		try {
			const res = await this.dashboardService.getCarrierCbm();
			runInAction(() => {
				this.cbmBreakdown = Array.isArray(res.data) ? res.data : [];
			});
		} catch (e) {
			runInAction(() => {
				this.cbmBreakdown = [];
			});
		}
	}

	// `silent` skips the completedLoading toggle so a background refresh doesn't blank the status cards.
	@action.bound
	async loadCompletedBreakdown({ silent = false } = {}) {
		// metric=late is semantically incompatible with "completed today" — short-circuit.
		if (this.activeMetric === 'late') {
			runInAction(() => {
				this.completedBreakdown = [];
				this.completedLoading = false;
			});
			return;
		}

		if (!silent) {
			runInAction(() => {
				this.completedLoading = true;
			});
		}

		const filter = {
			metric: this.activeMetric !== 'all' ? this.activeMetric : undefined,
			date: this.selectedDate || undefined,
		};

		try {
			const res = await this.dashboardService.getCompletedCount(filter);
			runInAction(() => {
				this.completedBreakdown = Array.isArray(res.data) ? res.data : [];
			});
		} catch (e) {
			runInAction(() => {
				this.completedBreakdown = [];
			});
		} finally {
			runInAction(() => {
				this.completedLoading = false;
			});
		}
	}

	@action.bound
	setSelectedWarehouse(id) {
		this.selectedWarehouse = id;
	}

	@action.bound
	setSelectedSeller(id) {
		this.selectedSeller = id;
	}

	@action.bound
	setSelectedShipmentType(id) {
		this.selectedShipmentType = id;
	}

	@action.bound
	setActiveStatus(key) {
		this.activeStatus = key;
	}

	@action.bound
	toggleMetric(key) {
		this.activeMetric = this.activeMetric === key ? 'all' : key;
	}

	@action.bound
	setSelectedDate(date) {
		this.selectedDate = date;
	}

	// ── Derived data ────────────────────────────────────────────────────────

	@computed
	get bySellerAndStatus() {
		return applyShipmentType(
			this.allOrders
				.filter(o => this.selectedSeller === 'all' || o.sellerId === this.selectedSeller)
				.filter(o => applyStatusFilter(o, this.activeStatus)),
			this.selectedShipmentType
		);
	}

	@computed
	get byWarehouseAndStatus() {
		return applyShipmentType(
			this.allOrders
				.filter(o => this.selectedWarehouse === 'all' || o.warehouseId === this.selectedWarehouse)
				.filter(o => applyStatusFilter(o, this.activeStatus)),
			this.selectedShipmentType
		);
	}

	// Shipment-type panel basis: every other axis applied, but NOT the shipment type itself —
	// same cross-axis convention as warehouseBasis / sellerBasis.
	@computed
	get shipmentTypeBasis() {
		return applyMetricDate(
			this.allOrders
				.filter(o => this.selectedSeller === 'all' || o.sellerId === this.selectedSeller)
				.filter(o => this.selectedWarehouse === 'all' || o.warehouseId === this.selectedWarehouse)
				.filter(o => applyStatusFilter(o, this.activeStatus)),
			this.activeMetric,
			this.selectedDate
		);
	}

	@computed
	get warehouseBasis() {
		return applyMetricDate(this.bySellerAndStatus, this.activeMetric, this.selectedDate);
	}

	@computed
	get sellerBasis() {
		return applyMetricDate(this.byWarehouseAndStatus, this.activeMetric, this.selectedDate);
	}

	// Name lookups built from allOrders — completedBreakdown rows carry only ids + count, no names.
	@computed
	get warehouseNameById() {
		const map = {};
		this.allOrders.forEach(o => {
			if (o.warehouseId && map[o.warehouseId] == null) map[o.warehouseId] = o.warehouseName;
		});
		return map;
	}

	@computed
	get sellerNameById() {
		const map = {};
		this.allOrders.forEach(o => {
			if (o.sellerId && map[o.sellerId] == null) map[o.sellerId] = o.sellerName;
		});
		return map;
	}

	// completedBreakdown is fetched pre-filtered by metric + date server-side, so the panels only
	// apply the cross-axis selection here (seller for the warehouse panel, warehouse for the seller panel).
	// Shipment type is a grouping dimension on the breakdown rows, so it is applied client-side.
	@computed
	get completedBySeller() {
		return applyShipmentType(
			this.completedBreakdown.filter(r => this.selectedSeller === 'all' || r.sellerId === this.selectedSeller),
			this.selectedShipmentType
		);
	}

	@computed
	get completedByWarehouse() {
		return applyShipmentType(
			this.completedBreakdown.filter(
				r => this.selectedWarehouse === 'all' || r.warehouseId === this.selectedWarehouse
			),
			this.selectedShipmentType
		);
	}

	// Cross-axis basis for the shipment-type panel in the 'completed' status: warehouse + seller
	// applied, shipment type deliberately not.
	@computed
	get completedByShipmentTypeBasis() {
		return this.completedBreakdown
			.filter(r => this.selectedSeller === 'all' || r.sellerId === this.selectedSeller)
			.filter(r => this.selectedWarehouse === 'all' || r.warehouseId === this.selectedWarehouse);
	}

	@computed
	get completedWarehouses() {
		const map = {};
		this.completedBySeller
			.filter(r => r.warehouseId)
			.forEach(r => {
				if (!map[r.warehouseId]) {
					const name = this.warehouseNameById[r.warehouseId];
					map[r.warehouseId] = { id: r.warehouseId, name: name != null ? name : 'Unknown', count: 0 };
				}
				map[r.warehouseId].count += r.count;
			});
		return Object.values(map).sort((a, b) => b.count - a.count);
	}

	@computed
	get completedSellers() {
		const map = {};
		this.completedByWarehouse.forEach(r => {
			if (!map[r.sellerId]) {
				const name = this.sellerNameById[r.sellerId];
				map[r.sellerId] = { id: r.sellerId, name: name != null ? name : 'Unknown', count: 0 };
			}
			map[r.sellerId].count += r.count;
		});
		return Object.values(map).sort((a, b) => b.count - a.count);
	}

	@computed
	get warehouses() {
		// Completed orders live in completedBreakdown, not allOrders — aggregate from there.
		if (this.activeStatus === 'completed') return this.completedWarehouses;
		const map = {};
		this.warehouseBasis
			.filter(o => o.warehouseId)
			.forEach(o => {
				if (!map[o.warehouseId]) map[o.warehouseId] = { id: o.warehouseId, name: o.warehouseName, count: 0 };
				map[o.warehouseId].count++;
			});
		return Object.values(map).sort((a, b) => b.count - a.count);
	}

	@computed
	get sellers() {
		if (this.activeStatus === 'completed') return this.completedSellers;
		const map = {};
		this.sellerBasis.forEach(o => {
			if (!map[o.sellerId]) map[o.sellerId] = { id: o.sellerId, name: o.sellerName, count: 0 };
			map[o.sellerId].count++;
		});
		return Object.values(map).sort((a, b) => b.count - a.count);
	}

	@computed
	get filtered() {
		return applyMetricDate(
			applyShipmentType(
				this.allOrders
					.filter(o => this.selectedSeller === 'all' || o.sellerId === this.selectedSeller)
					.filter(o => this.selectedWarehouse === 'all' || o.warehouseId === this.selectedWarehouse),
				this.selectedShipmentType
			),
			this.activeMetric,
			this.selectedDate
		);
	}

	// Metric badge base: only seller + warehouse + shipment type — NOT date. Selecting a ship-by-date
	// pill must not zero out the Late Shipments badge (fix from standalone commit d9e1a675).
	@computed
	get metricBase() {
		return applyShipmentType(
			this.allOrders
				.filter(o => this.selectedSeller === 'all' || o.sellerId === this.selectedSeller)
				.filter(o => this.selectedWarehouse === 'all' || o.warehouseId === this.selectedWarehouse),
			this.selectedShipmentType
		);
	}

	@computed
	get onHoldCount() {
		return this.metricBase.filter(o => o.isOnHold).length;
	}

	@computed
	get lateCount() {
		const today = todayIso();
		return this.metricBase.filter(o => o.shipByDate && toDate(o.shipByDate) < today).length;
	}

	@computed
	get primeCount() {
		return this.metricBase.filter(o => o.isPrime).length;
	}

	@computed
	get shipByDateRows() {
		const map = {};
		applyMetricDate(
			applyShipmentType(
				this.allOrders
					.filter(o => this.selectedSeller === 'all' || o.sellerId === this.selectedSeller)
					.filter(o => this.selectedWarehouse === 'all' || o.warehouseId === this.selectedWarehouse)
					.filter(o => applyStatusFilter(o, this.activeStatus)),
				this.selectedShipmentType
			),
			this.activeMetric,
			null
		).forEach(o => {
			const d = toDate(o.shipByDate);
			if (d) map[d] = (map[d] || 0) + 1;
		});
		return Object.entries(map).map(([date, count]) => ({ date, count }));
	}

	@computed
	get completedToday() {
		return applyShipmentType(this.completedByShipmentTypeBasis, this.selectedShipmentType).reduce(
			(sum, r) => sum + r.count,
			0
		);
	}

	@computed
	get totals() {
		const working = this.filtered.reduce(
			(acc, o) => ({ ...acc, [o.status]: (acc[o.status] != null ? acc[o.status] : 0) + 1 }),
			{ entered: 0, assigned: 0, picking: 0 }
		);
		const completed = this.completedToday;
		return {
			...working,
			completed,
			overall: working.entered + working.assigned + working.picking + completed,
		};
	}

	@computed
	get completedPct() {
		const { overall } = this.totals;
		if (!overall) return null;
		return Math.round((this.completedToday / overall) * 100);
	}

	// Client-side aggregation of cbmBreakdown — mirrors the standalone commit's `cbmAgg` block.
	// All filtering (warehouse, seller, status, metric, date) happens here so the BE breakdown
	// can stay loaded once on mount.
	@computed
	get carrierCbm() {
		const today = todayIso();
		const agg = {};
		applyShipmentType(this.cbmBreakdown, this.selectedShipmentType)
			.filter(r => this.selectedWarehouse === 'all' || r.warehouseId === this.selectedWarehouse)
			.filter(r => this.selectedSeller === 'all' || r.sellerId === this.selectedSeller)
			.filter(r => applyStatusFilter(r, this.activeStatus))
			.filter(r => this.activeMetric !== 'onHold' || r.isOnHold)
			.filter(r => this.activeMetric !== 'late' || (r.shipByDate && toDate(r.shipByDate) < today))
			.filter(r => this.activeMetric !== 'prime' || r.isPrime)
			.filter(r => !this.selectedDate || toDate(r.shipByDate) === this.selectedDate)
			.forEach(r => {
				agg[r.carrierType] = (agg[r.carrierType] != null ? agg[r.carrierType] : 0) + Number(r.cbm);
			});
		return Object.entries(agg)
			.map(([carrierType, cbm]) => ({ carrierType, cbm: Math.round(cbm * 100) / 100 }))
			.sort((a, b) => b.cbm - a.cbm);
	}

	@computed
	get chartData() {
		const byWarehouse = {};
		this.filtered.forEach(o => {
			const key = o.warehouseId != null ? o.warehouseId : '__unassigned__';
			const name = o.warehouseName != null ? o.warehouseName : 'Unassigned';
			if (!byWarehouse[key]) {
				byWarehouse[key] = {
					warehouse: name,
					warehouseId: o.warehouseId,
					entered: 0,
					assigned: 0,
					picking: 0,
					completed: 0,
					overall: 0,
				};
			}
			byWarehouse[key][o.status] = (byWarehouse[key][o.status] != null ? byWarehouse[key][o.status] : 0) + 1;
			if (WORKING_STATUSES.includes(o.status)) byWarehouse[key].overall++;
		});

		applyShipmentType(this.completedByShipmentTypeBasis, this.selectedShipmentType).forEach(r => {
			const key = r.warehouseId != null ? r.warehouseId : '__unassigned__';
			if (byWarehouse[key]) {
				byWarehouse[key].completed += r.count;
				byWarehouse[key].overall += r.count;
			}
		});

		const rows = Object.values(byWarehouse)
			.filter(row => row[this.activeStatus] != null && row[this.activeStatus] > 0)
			.sort((a, b) => a.warehouse.localeCompare(b.warehouse));
		return rows.map((row, i) => {
			const mapped = this.warehouseColorMap[row.warehouseId];
			const color = mapped != null ? mapped : WAREHOUSE_COLORS[i % WAREHOUSE_COLORS.length];
			const activeValue = row[this.activeStatus];
			return {
				warehouse: row.warehouse,
				value: activeValue != null ? activeValue : 0,
				color,
				...(this.activeStatus === 'overall' && {
					segments: [
						{ status: 'entered', value: row.entered != null ? row.entered : 0 },
						{ status: 'assigned', value: row.assigned != null ? row.assigned : 0 },
						{ status: 'picking', value: row.picking != null ? row.picking : 0 },
						{ status: 'completed', value: row.completed != null ? row.completed : 0 },
					].filter(seg => seg.value > 0),
				}),
			};
		});
	}

	@computed
	get warehouseItems() {
		const allCount =
			this.activeStatus === 'completed'
				? this.completedBySeller.reduce((sum, r) => sum + r.count, 0)
				: this.warehouseBasis.length;
		return [
			{ id: 'all', name: 'All', color: WAREHOUSE_COLORS[0], count: allCount },
			...this.warehouses.map(w => {
				const mapped = this.warehouseColorMap[w.id];
				return {
					...w,
					color: mapped != null ? mapped : WAREHOUSE_COLORS[0],
				};
			}),
		];
	}

	@computed
	get sellerItems() {
		const allCount =
			this.activeStatus === 'completed'
				? this.completedByWarehouse.reduce((sum, r) => sum + r.count, 0)
				: this.sellerBasis.length;
		return [{ id: 'all', name: 'All', count: allCount }, ...this.sellers];
	}

	// Completed orders live in completedBreakdown, not allOrders — count from whichever set the
	// active status refers to, mirroring warehouseItems / sellerItems.
	@computed
	get shipmentTypeItems() {
		const rows =
			this.activeStatus === 'completed' ? this.completedByShipmentTypeBasis : this.shipmentTypeBasis;
		const countOf = predicate =>
			this.activeStatus === 'completed'
				? rows.filter(predicate).reduce((sum, r) => sum + r.count, 0)
				: rows.filter(predicate).length;
		return [
			{ id: 'all', name: 'All', count: countOf(() => true) },
			{ id: 'ltl', name: 'LTL', count: countOf(r => r.isLtl) },
			{ id: 'parcel', name: 'Parcel', count: countOf(r => !r.isLtl) },
		];
	}

	@computed
	get warehouseLabel() {
		if (this.selectedWarehouse === 'all') {
			// Warehouse-scoped users have no Warehouses panel; their data is implicitly filtered to
			// their own warehouse, so show its name instead of the misleading "All Warehouses".
			if (isWarehouse(this.rootStore)) {
				const name = this.rootStore.organization && this.rootStore.organization.applicationTitle;
				return name != null ? name : 'All Warehouses';
			}
			return 'All Warehouses';
		}
		const match = this.warehouses.find(w => w.id === this.selectedWarehouse);
		return match && match.name != null ? match.name : 'All Warehouses';
	}

	@computed
	get sellerLabel() {
		if (this.selectedSeller === 'all') {
			// Seller-scoped users have no Sellers panel; their data is implicitly filtered to their
			// own seller, so show its name instead of the misleading "All Sellers".
			if (isSeller(this.rootStore)) {
				const name = this.rootStore.organization && this.rootStore.organization.applicationTitle;
				return name != null ? name : 'All Sellers';
			}
			return 'All Sellers';
		}
		const match = this.sellers.find(s => s.id === this.selectedSeller);
		return match && match.name != null ? match.name : 'All Sellers';
	}

	@computed
	get selectedWarehouseName() {
		if (this.selectedWarehouse === 'all') return 'all';
		const match = this.warehouses.find(w => w.id === this.selectedWarehouse);
		return match && match.name != null ? match.name : 'all';
	}

	// ── Auto refresh ────────────────────────────────────────────────────────

	startAutoRefresh = () => {
		this.stopAutoRefresh();
		this.refreshTimerId = setInterval(() => {
			this.refreshAll();
		}, AUTO_REFRESH_INTERVAL_MS);
	};

	stopAutoRefresh = () => {
		if (this.refreshTimerId) {
			clearInterval(this.refreshTimerId);
			this.refreshTimerId = null;
		}
	};

	// No suspend()/resume() here — a background refresh must not blank the chart, badges and cards.
	@action.bound
	async refreshAll() {
		await Promise.all([
			this.loadOrdersSummary(),
			this.loadCbmBreakdown(),
			this.loadCompletedBreakdown({ silent: true }),
		]);
	}

	// ── Grid view ───────────────────────────────────────────────────────────

	@action.bound
	async openGridView() {
		this.gridViewOpen = true;
		this.gridLoading = true;
		this.gridError = null;
		this.gridOrders = [];
		this.stopAutoRefresh();

		const filter = {
			warehouseId: this.selectedWarehouse !== 'all' ? this.selectedWarehouse : undefined,
			sellerId: this.selectedSeller !== 'all' ? this.selectedSeller : undefined,
			date: this.selectedDate || undefined,
			metric: this.activeMetric !== 'all' ? this.activeMetric : undefined,
			status: this.activeStatus !== 'overall' ? this.activeStatus : undefined,
			isLtl: this.selectedShipmentType !== 'all' ? this.selectedShipmentType === 'ltl' : undefined,
		};

		try {
			const res = await this.dashboardService.getOrders(filter);
			runInAction(() => {
				this.gridOrders = res.data || [];
			});
		} catch (e) {
			runInAction(() => {
				this.gridError = 'Failed to load orders';
			});
		} finally {
			runInAction(() => {
				this.gridLoading = false;
			});
		}
	}

	@action.bound
	closeGridView() {
		this.gridViewOpen = false;
		this.startAutoRefresh();
	}

	exportGridToCsv = ({ showSeller, showWarehouse }) => {
		const csv = toCSV(this.gridOrders, showSeller, showWarehouse);
		downloadCSV(csv);
	};

	@action.bound
	destroy() {
		this.stopAutoRefresh();
		if (this.completedBreakdownReactionDisposer) {
			this.completedBreakdownReactionDisposer();
			this.completedBreakdownReactionDisposer = null;
		}
	}
}

// ── CSV helpers (ported verbatim from dashboards/app/src/components/GridViewModal.jsx:26-60) ──

function escapeCSV(val) {
	if (val == null) return '';
	const s = /^[=+\-@]/.test(String(val)) ? "'" + String(val) : String(val);
	if (s.includes(',') || s.includes('"') || s.includes('\n')) {
		return '"' + s.replace(/"/g, '""') + '"';
	}
	return s;
}

function toCSV(rows, showSeller, showWarehouse) {
	const cols = [
		{ header: 'Order #', get: o => o.fulfillmentOrderId },
		showSeller ? { header: 'Seller', get: o => (o.sellerName != null ? o.sellerName : '') } : null,
		showWarehouse ? { header: 'Warehouse', get: o => (o.warehouseName != null ? o.warehouseName : '') } : null,
		{ header: 'Status', get: o => (o.status != null ? o.status : '') },
		{ header: 'Channel', get: o => (o.channel != null ? o.channel : '') },
		{ header: 'Type', get: o => (o.isLtl ? 'LTL' : 'Parcel') },
		{ header: 'Ship By', get: o => (o.shipByDate ? String(o.shipByDate).slice(0, 10) : '') },
		{ header: 'On Hold', get: o => (o.isOnHold ? 'Yes' : '') },
		{ header: 'Prime', get: o => (o.isPrime ? 'Yes' : '') },
	].filter(Boolean);

	const header = cols.map(c => escapeCSV(c.header)).join(',');
	const lines = rows.map(o => cols.map(c => escapeCSV(c.get(o))).join(','));
	return [header, ...lines].join('\r\n');
}

function downloadCSV(csv) {
	const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
	a.click();
	URL.revokeObjectURL(url);
}

export default DashboardViewStore;
